<template>
  <div class="review">
    <div class="query-item">
      <div class="search-l">
        <div v-if="!store.roles.includes('carEnterprise')" style="margin-right: 30px">
          测试主体
          <a-input class="inp ml24" placeholder="请输入" v-model:value="query.deptName"></a-input>
        </div>
        <div>
          测试牌照
          <a-input class="inp ml24" placeholder="请输入" v-model:value="query.testCarNum"></a-input>
        </div>
        <div class="ml30">
          脱离时间
          <!-- 替换为月份范围选择器 -->
          <a-range-picker
              class="ml24"
              :mode="['month', 'month']"
              format="YYYY-MM"
              valueFormat="YYYY-MM"
              @change="monthChange"
              v-model:value="monthRange"
          />
        </div>
        <a-button class="ml24" @click="search" type="primary" ghost>查询</a-button>
        <a-button class="ml24" @click="reset" type="primary" ghost>重置</a-button>
        <div></div>
      </div>
    </div>
    <a-table
        :columns="columns"
        :row-key="(record) => record.id"
        :data-source="dataSource"
        class="mt23"
        size="middle"
        :pagination="pagination"
        :scroll="{ x: 3000, y: 'auto' }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'monthYear'">
          {{ record.monthYear }}
        </template>
        <template v-if="column.key === 'totalCount'">
          <span>{{ record.totalCount }}</span>
        </template>
        <template v-if="column.key === 'action'">
          <span><a class="mr10" @click="detail(record)">详情</a></span>
        </template>
      </template>
    </a-table>
    <!-- 详情抽屉组件 -->
    <DepartureData
        :open="drawerVisible"
        :record="selectedRecord"
        :query="query"
        @close="handleDrawerClose"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue'
import useUserStore from '@/store/modules/user'

import DepartureData from './components/DepartureData.vue'

// 接收父组件传递的查询参数
const props = defineProps({
  query: {
    type: Object,
    required: true
  }
})

// 定义事件
const emit = defineEmits(['data-update'])

const store = useUserStore()

let query = reactive({
  testCarNum: "",
  startDateStr: "",
  endDateStr: "",
  deptName: "",
});

const search = () => {
  pagination.current = 1;
  getData();
};

const monthRange = ref([]);

const monthChange = (value) => {
  console.log('选择的月份范围:', value);
  query.startDateStr = value[0];
  query.endDateStr = value[1];
};

// 修改reset函数中的相关代码
const reset = () => {
  query.testCarNum = "";
  query.startDateStr = "";
  query.endDateStr = "";
  query.deptName = "";
  monthRange.value = []; // 重置月份范围
  pagination.current = 1;
  getData();
};

// 分页配置
const pagination = reactive({
  total: 0,
  size: "middle",
  defaultPageSize: 10,
  current: 1,
  pageSize: 10,
  showSizeChanger: true,
  showQuickJumper: false,
  pageSizeOptions: ["10", "20", "30"],
  showTotal: (total) => `共有 ${total} 条数据`,
  onShowSizeChange: (current, pageSize) => {
    pagination.pageSize = pageSize
    getData()
  },
  onChange: (page) => {
    pagination.current = page
    getData()
  }
})

// 表格数据源
const dataSource = ref([])

// 添加抽屉相关状态
const drawerVisible = ref(false)
const selectedRecord = ref(null)

// 实现详情按钮点击事件
const detail = (record) => {
  selectedRecord.value = record
  drawerVisible.value = true
}

// 关闭抽屉的处理函数
const handleDrawerClose = () => {
  drawerVisible.value = false
  selectedRecord.value = null
}

// 表格列配置
const columns = [
  {
    title: "月份",
    dataIndex: "monthYear",
    key: "monthYear",
    width: 120,
    fixed: "left",
    ellipsis: true
  },
  {
    title: "测试主体",
    dataIndex: "deptName",
    key: "deptName",
    width: 150,
    fixed: "left"
  },
  {
    title: "测试牌照",
    dataIndex: "testCarNum",
    key: "testCarNum",
    width: 150,
    fixed: "left"
  },
  {
    title: "脱离总次数",
    dataIndex: "totalCount",
    key: "totalCount",
    width: 120,
    fixed: "left"
  },
  {
    title: "脱离时场景分类",
    children: [
      {
        title: "变更车道时",
        dataIndex: "sceneCategory.changeLane",
        key: "sceneCategory.changeLane",
        width: 100,
        align: "center"
      },
      {
        title: "掉头时",
        dataIndex: "sceneCategory.turnAround",
        key: "sceneCategory.turnAround",
        width: 100,
        align: "center"
      },
      {
        title: "直行时",
        dataIndex: "sceneCategory.straight",
        key: "sceneCategory.straight",
        width: 100,
        align: "center"
      },
      {
        title: "路口左转弯时",
        dataIndex: "sceneCategory.leftTurn",
        key: "sceneCategory.leftTurn",
        width: 120,
        align: "center"
      },
      {
        title: "路口右转弯时",
        dataIndex: "sceneCategory.rightTurn",
        key: "sceneCategory.rightTurn",
        width: 120,
        align: "center"
      },
      {
        title: "遇到信号灯时",
        dataIndex: "sceneCategory.trafficLight",
        key: "sceneCategory.trafficLight",
        width: 120,
        align: "center"
      },
      {
        title: "会车时",
        dataIndex: "sceneCategory.meeting",
        key: "sceneCategory.meeting",
        width: 100,
        align: "center"
      },
      {
        title: "其他",
        dataIndex: "sceneCategory.other",
        key: "sceneCategory.other",
        width: 100,
        align: "center"
      }
    ]
  },
  {
    title: "脱离时主要交通参与者类型",
    children: [
      {
        title: "大型客车",
        dataIndex: "participantType.largeBus",
        key: "participantType.largeBus",
        width: 100,
        align: "center"
      },
      {
        title: "中大型货车",
        dataIndex: "participantType.largeTruck",
        key: "participantType.largeTruck",
        width: 120,
        align: "center"
      },
      {
        title: "特种车",
        dataIndex: "participantType.specialVehicle",
        key: "participantType.specialVehicle",
        width: 100,
        align: "center"
      },
      {
        title: "非机动车",
        dataIndex: "participantType.nonMotorVehicle",
        key: "participantType.nonMotorVehicle",
        width: 100,
        align: "center"
      },
      {
        title: "行人",
        dataIndex: "participantType.pedestrian",
        key: "participantType.pedestrian",
        width: 100,
        align: "center"
      },
      {
        title: "其他",
        dataIndex: "participantType.other",
        key: "participantType.other",
        width: 100,
        align: "center"
      }
    ]
  },
  {
    title: "脱离时主要交通设施",
    children: [
      {
        title: "锥桶",
        dataIndex: "facilityType.cones",
        key: "facilityType.cones",
        width: 100,
        align: "center"
      },
      {
        title: "隔离护栏",
        dataIndex: "facilityType.barrier",
        key: "facilityType.barrier",
        width: 100,
        align: "center"
      },
      {
        title: "施工带",
        dataIndex: "facilityType.constructionZone",
        key: "facilityType.constructionZone",
        width: 100,
        align: "center"
      },
      {
        title: "其他",
        dataIndex: "facilityType.other",
        key: "facilityType.other",
        width: 100,
        align: "center"
      }
    ]
  },
  {
    title: "脱离类别",
    children: [
      {
        title: "主动脱离",
        dataIndex: "departureType.active",
        key: "departureType.active",
        width: 100,
        align: "center"
      },
      {
        title: "被动脱离",
        dataIndex: "departureType.passive",
        key: "departureType.passive",
        width: 100,
        align: "center"
      }
    ]
  },
  {
    title: "脱离原因",
    children: [
      {
        title: "系统故障",
        dataIndex: "departureReason.systemFailure",
        key: "departureReason.systemFailure",
        width: 100,
        align: "center"
      },
      {
        title: "策略缺陷",
        dataIndex: "departureReason.strategyDefect",
        key: "departureReason.strategyDefect",
        width: 100,
        align: "center"
      },
      {
        title: "人工安全防御",
        dataIndex: "departureReason.safetyDefense",
        key: "departureReason.safetyDefense",
        width: 120,
        align: "center"
      },
      {
        title: "人为接管",
        dataIndex: "departureReason.manualTakeover",
        key: "departureReason.manualTakeover",
        width: 100,
        align: "center"
      },
      {
        title: "其他",
        dataIndex: "departureReason.other",
        key: "departureReason.other",
        width: 100,
        align: "center"
      }
    ]
  },
  {
    title: "人工介入方式",
    children: [
      {
        title: "自动踏板",
        dataIndex: "interventionType.pedal",
        key: "interventionType.pedal",
        width: 100,
        align: "center"
      },
      {
        title: "方向盘",
        dataIndex: "interventionType.steering",
        key: "interventionType.steering",
        width: 100,
        align: "center"
      },
      {
        title: "硬或软开关介入",
        dataIndex: "interventionType.switch",
        key: "interventionType.switch",
        width: 120,
        align: "center"
      }
    ]
  },
  {
    title: "操作",
    key: "action",
    width: 100,
    fixed: "right",
    align: "center"
  }
]



// 生成模拟数据
const generateMockData = () => {
  const mockData = []
  const now = new Date()
  const deptNames = ['唯速智驾', '智能出行', '自动驾驶科技', '未来交通', '创新驾驶']
  const testCarNums = ['RL825203', 'TS736402', 'ZD918527', 'JC364592', 'CX587219']

  // 生成过去12个月的模拟数据
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const monthYear = `${year}年${month}月`

    // 为每个月生成多条记录，模拟不同测试主体和牌照的数据
    const recordCount = Math.floor(Math.random() * 3) + 1 // 每个月1-3条记录

    for (let j = 0; j < recordCount; j++) {
      const sceneCategory = {
        changeLane: Math.floor(Math.random() * 20),
        turnAround: Math.floor(Math.random() * 20),
        straight: Math.floor(Math.random() * 30),
        leftTurn: Math.floor(Math.random() * 15),
        rightTurn: Math.floor(Math.random() * 15),
        trafficLight: Math.floor(Math.random() * 10),
        meeting: Math.floor(Math.random() * 8),
        other: Math.floor(Math.random() * 5)
      }

      const participantType = {
        largeBus: Math.floor(Math.random() * 20),
        largeTruck: Math.floor(Math.random() * 20),
        specialVehicle: Math.floor(Math.random() * 15),
        nonMotorVehicle: Math.floor(Math.random() * 15),
        pedestrian: Math.floor(Math.random() * 10),
        other: Math.floor(Math.random() * 5)
      }

      const facilityType = {
        cones: Math.floor(Math.random() * 15),
        barrier: Math.floor(Math.random() * 15),
        constructionZone: Math.floor(Math.random() * 10),
        other: Math.floor(Math.random() * 5)
      }

      const departureType = {
        active: Math.floor(Math.random() * 30),
        passive: Math.floor(Math.random() * 30)
      }

      const departureReason = {
        systemFailure: Math.floor(Math.random() * 20),
        strategyDefect: Math.floor(Math.random() * 20),
        safetyDefense: Math.floor(Math.random() * 15),
        manualTakeover: Math.floor(Math.random() * 15),
        other: Math.floor(Math.random() * 5)
      }

      const interventionType = {
        pedal: Math.floor(Math.random() * 20),
        steering: Math.floor(Math.random() * 20),
        switch: Math.floor(Math.random() * 15)
      }

      // 计算总脱离次数
      const totalCount = Object.values(sceneCategory).reduce((sum, val) => sum + val, 0)

      mockData.push({
        id: `${year}${month.toString().padStart(2, '0')}${j}`,
        monthYear,
        deptName: deptNames[Math.floor(Math.random() * deptNames.length)],
        testCarNum: testCarNums[Math.floor(Math.random() * testCarNums.length)],
        totalCount,
        sceneCategory,
        participantType,
        facilityType,
        departureType,
        departureReason,
        interventionType
      })
    }
  }

  return mockData
}

// 获取数据
const getData = () => {
  // 在实际项目中，这里应该调用API获取数据
  // 模拟API请求
  console.log('查询参数:', props.query)

  // 生成模拟数据
  const mockData = generateMockData()

  // 根据页码和每页数量截取数据
  const startIndex = (pagination.current - 1) * pagination.pageSize
  const endIndex = startIndex + pagination.pageSize

  dataSource.value = mockData.slice(startIndex, endIndex)
  pagination.total = mockData.length

  // 触发数据更新事件
  emit('data-update', 'departure')
}

// 监听查询参数变化
const propsWatcher = watch(() => props.query, () => {
  pagination.current = 1
  getData()
}, { deep: true })

onMounted(() => {
  getData()
})
</script>

<style lang="less" scoped>
.review {
  padding: 20px;
  height: calc(100vh - 70px - 57px);
  background-color: #ffffff;
  overflow: auto;
}

.inp {
  width: 13.5vw;
}

.query-item {
  display: flex;
  justify-content: space-between;

  .search-l {
    display: flex;
  }

  .search-r {
    display: flex;
  }
}

/deep/ .mr10 {
  color: #1890ff !important;
}

</style>